import { useState, useEffect, createContext, useContext } from "react";
import { adminMe, adminLogout } from "../lib/adminApi";
import useProductLogo from "../lib/useProductLogo";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import {
  ClipboardList, LogOut, Briefcase, Wrench, Inbox,
  ShieldCheck, History, Lock, Sparkles, UserCircle, FileSearch, ChevronRight,
  HeartPulse, Plug, SlidersHorizontal, Building2, Server, BookOpen, ClipboardCheck,
} from "lucide-react";
import ProfileDialog from "../components/ProfileDialog";
import {
  APP_SMARTEVALUATE, APP_SMARTBID, APP_SMARTIMPROVE, APP_SMARTEWM, APP_SMARTINSPECT,
  hasApp, hasConsultantWorkspace, hasSmartEwm,
} from "../lib/applications";

const ConsultantCtx = createContext(null);
export const useConsultant = () => useContext(ConsultantCtx);

// PRIVA Governance — runtime operational AI monitoring + governance for
// every PrimeS4 AI application. Lives in this runtime sidebar (NOT the
// admin configuration sidebar) so the "control plane vs runtime plane"
// architecture intent is reflected in the navigation IA. Admin-only —
// the section is conditionally rendered based on `user.role === "admin"`
// at render time.
const PRIVA_GOVERNANCE_GROUP = {
  label: "PRIVA Governance",
  testId: "consultant-nav-group-priva-governance",
  adminOnly: true,
  collapsible: true,      // collapsible group (like the Admin panel modules)
  groupIcon: ShieldCheck, // header icon to match the Admin panel group style
  items: [
    { type: "subheader", label: "Platform Administration" },
    { to: "/admin/priva/clients", label: "Client Master", icon: Building2 },
    { to: "/admin/priva/sap-systems", label: "SAP System Master", icon: Server },
    { to: "/admin/priva/sap-connections", label: "SAP Connections", icon: Plug },
    { type: "subheader", label: "PRIVA Intelligence" },
    { to: "/admin/priva/signal-health", label: "Signal Health", icon: HeartPulse },
    { to: "/admin/priva/signal-catalog", label: "Signal Catalog", icon: BookOpen },
    { to: "/admin/priva/runs", label: "Recent PRIVA Runs", icon: History },
    { type: "subheader", label: "Application Configuration" },
    { to: "/admin/priva/smartewm-config", label: "SmartEWM Configuration", icon: SlidersHorizontal },
  ],
};

// Sidebar IA — grouped by purpose so the workspace, governance, and
// Client Output modules remain discoverable as the platform scales.
// Lightweight (no collapsible groups yet) — labels act as visual
// section headers.
const NAV_GROUPS = [
  {
    label: "Workspaces",
    items: [
      { to: "/consultant/assessments", label: "SmartEvaluate Dashboard", icon: ClipboardList, requiresApp: APP_SMARTEVALUATE },
      { to: "/consultant/smartbid", label: "SmartBid Dashboard", icon: FileSearch, requiresApp: APP_SMARTBID },
      { to: "/consultant/smartimprove", label: "SmartImprove Dashboard", icon: Wrench, requiresApp: APP_SMARTIMPROVE },
      { to: "/consultant/smartinspect", label: "SmartInspect Dashboard", icon: ClipboardCheck, requiresApp: APP_SMARTINSPECT },
      { to: "/app/smartewmbot", label: "SmartEWM", icon: Sparkles, requiresApp: APP_SMARTEWM },
    ],
  },
  PRIVA_GOVERNANCE_GROUP,
  {
    label: "Client Output",
    items: [
      { to: "/consultant/demo-requests", label: "Demo Requests", icon: Inbox },
    ],
  },
];

// Client Access Management — restricted sidebar for the Client role. Clients
// reuse this exact shell (same layout/branding) but only see their own
// evaluations + profile + logout. No workspace/governance/admin nav.
const CLIENT_NAV_GROUPS = [
  {
    label: "Client Access",
    items: [
      { to: "/transformation/evaluations", label: "My Evaluations", icon: ClipboardList },
    ],
  },
];

// For now, consultants share the admin JWT. Simulates separate access via route segregation.
export function ConsultantLayout() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [profileOpen, setProfileOpen] = useState(false);
  // Collapsible nav groups (e.g. PRIVA Governance). Default collapsed; a group
  // auto-expands only when one of its routes is currently active.
  const [openGroups, setOpenGroups] = useState(() => ({
    "PRIVA Governance": window.location.pathname.startsWith("/admin/priva"),
  }));
  const navigate = useNavigate();
  const location = useLocation();
  const logo = useProductLogo();
  // Client View hides every internal control — sidebar, nav, logout — so the
  // page is safe to screen-share directly with the client.
  const clientViewActive = new URLSearchParams(location.search).get("view") === "client";

  useEffect(() => {
    adminMe()
      .then(u => {
        setUser(u);
        setChecking(false);
        // Access-aware segregation: a user with NO consultant-workspace app
        // (SmartEvaluate / SmartImprove / WarehouseX) must not see this shell.
        // SmartEWM-only users are bounced into the SmartEWM app; anyone with
        // no mapped application is sent back to the portal.
        const role = (u?.role || "").toLowerCase();
        // Client Access users reuse this shell (restricted nav). Everyone else
        // with NO consultant-workspace app is bounced as before.
        if (role !== "admin" && role !== "client" && !hasConsultantWorkspace(u)) {
          navigate(hasSmartEwm(u) ? "/app/smartewmbot" : "/transformation", { replace: true });
        }
      })
      .catch(() => { setChecking(false); navigate("/transformation"); });
  }, [navigate]);

  const logout = async () => {
    await adminLogout();
    navigate("/transformation");
  };

  if (checking) {
    return <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center"><p className="text-sm text-[#6B7280]">Loading...</p></div>;
  }
  if (!user) return null;

  // Role-specific workspace branding. Consultants keep the original teal
  // theme; Demo users get a distinct PrimeS4-blue sidebar with a black
  // active-state so it's visually obvious that this is a demo environment.
  const isDemo = (user.role || "").toLowerCase() === "demo";
  const isClient = (user.role || "").toLowerCase() === "client";
  const navGroups = isClient ? CLIENT_NAV_GROUPS : NAV_GROUPS;
  const brandTo = isClient ? "/transformation/evaluations" : "/consultant/assessments";
  const theme = isDemo
    ? {
        title: "WarehouseX — Demo",
        sidebarBg: "bg-[#025979]",
        activeBg: "bg-[#03738C]",
        activeText: "text-white",
        hoverBg: "hover:bg-[#03738C]/60",
        mutedText: "text-white/70",
        borderColor: "border-white/10",
      }
    : {
        title: "WarehouseX Consultant",
        sidebarBg: "bg-[#111827]",
        activeBg: "bg-[#025979]",
        activeText: "text-white",
        hoverBg: "hover:bg-gray-800",
        mutedText: "text-gray-400",
        borderColor: "border-white/10",
      };

  return (
    <ConsultantCtx.Provider value={user}>
      <div className="min-h-screen bg-[#F8FAFC] flex">
        {clientViewActive ? null : (
        <aside
          className={`sticky top-0 self-start h-screen w-56 ${theme.sidebarBg} flex flex-col shrink-0`}
          data-testid="consultant-sidebar"
          data-workspace-role={isDemo ? "demo" : "consultant"}
        >
          <div className={`px-4 py-4 border-b ${theme.borderColor}`}>
            <NavLink
              to={brandTo}
              className="flex items-center gap-2 focus:outline-none"
              data-testid="consultant-sidebar-brand"
            >
              {logo ? (
                <img loading="lazy"
                  src={logo}
                  alt="Platform logo"
                  className="h-8 w-auto object-contain"
                  data-testid="consultant-sidebar-brand-logo"
                />
              ) : (
                <>
                  <Briefcase className="w-4 h-4 text-white" strokeWidth={1.5} />
                  <p
                    className="text-sm font-bold text-white"
                    style={{ fontFamily: "Chivo, sans-serif" }}
                    data-testid="workspace-title"
                  >
                    {theme.title}
                  </p>
                </>
              )}
            </NavLink>
            <p className={`text-[10px] mt-1.5 ${theme.mutedText}`}>{user.email}</p>
          </div>
          <nav className="flex-1 py-2 overflow-y-auto">
            {navGroups.filter(
              (g) => !g.adminOnly || (user.role || "").toLowerCase() === "admin",
            ).map((group) => {
              // Hide (not disable) application items the user cannot access.
              const items = group.items.filter(
                (n) => !n.requiresApp || hasApp(user, n.requiresApp),
              );
              if (items.length === 0) return null;
              const isCollapsible = !!group.collapsible;
              const isOpen = !isCollapsible || !!openGroups[group.label];
              const groupTestId = group.testId || `consultant-nav-group-${group.label.replace(/\s+/g, "-").toLowerCase()}`;
              return (
              <div key={group.label} className="mb-2">
                {isCollapsible ? (
                  <button
                    type="button"
                    onClick={() => setOpenGroups((s) => ({ ...s, [group.label]: !s[group.label] }))}
                    aria-expanded={isOpen}
                    data-testid={groupTestId}
                    className={`w-full flex items-center gap-2 px-4 pt-3 pb-1 text-[10px] uppercase tracking-wider font-semibold ${theme.mutedText} opacity-70 hover:opacity-100 hover:text-white transition-colors`}
                  >
                    {group.groupIcon && <group.groupIcon className="w-3.5 h-3.5" strokeWidth={1.5} />}
                    <span className="flex-1 text-left">{group.label}</span>
                    <ChevronRight className={`w-3.5 h-3.5 transition-transform duration-200 ${isOpen ? "rotate-90" : ""}`} strokeWidth={2} />
                  </button>
                ) : (
                  <p
                    className={`px-4 pt-3 pb-1 text-[10px] uppercase tracking-wider font-semibold ${theme.mutedText} opacity-70`}
                    data-testid={groupTestId}
                  >
                    {group.label}
                  </p>
                )}
                {isOpen && items.map((n) => {
                  if (n.type === "subheader") {
                    return (
                      <p
                        key={`sub-${n.label}`}
                        className={`px-4 pt-3 pb-1 text-[9px] uppercase tracking-widest font-semibold ${theme.mutedText} opacity-50`}
                        data-testid={`consultant-nav-subheader-${n.label.replace(/\s+/g, "-").toLowerCase()}`}
                      >
                        {n.label}
                      </p>
                    );
                  }
                  if (n.disabled) {
                    return (
                      <div
                        key={n.to}
                        title="Coming soon"
                        className={`flex items-center gap-2.5 px-4 py-2 text-xs font-medium ${theme.mutedText} opacity-50 cursor-not-allowed select-none`}
                        data-testid={`consultant-nav-${n.to.replace(/\W+/g, '-')}`}
                        data-disabled="true"
                      >
                        <n.icon className="w-3.5 h-3.5" strokeWidth={1.5} />
                        <span className="flex-1">{n.label}</span>
                        <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-wider opacity-90">
                          <Lock className="w-2.5 h-2.5" />
                          Soon
                        </span>
                      </div>
                    );
                  }
                  return (
                    <NavLink
                      key={n.to}
                      to={n.to}
                      className={({ isActive }) =>
                        `flex items-center gap-2.5 px-4 py-2 text-xs font-medium transition-colors ${
                          isActive
                            ? `${theme.activeBg} ${theme.activeText}`
                            : `${theme.mutedText} hover:text-white ${theme.hoverBg}`
                        }`
                      }
                    >
                      <n.icon className="w-3.5 h-3.5" strokeWidth={1.5} />
                      {n.label}
                    </NavLink>
                  );
                })}
              </div>
              );
            })}
          </nav>
          <div className={`p-3 border-t ${theme.borderColor} space-y-2`}>
            <p
              data-testid="sidebar-powered-by"
              className={`text-[10px] leading-snug ${theme.mutedText} opacity-70 select-none`}
            >
              Powered by PRIVA Operational Intelligence
            </p>
            <button
              onClick={() => setProfileOpen(true)}
              className={`flex items-center gap-2 text-xs ${theme.mutedText} hover:text-white transition-colors`}
              data-testid="consultant-my-profile-btn"
            >
              <UserCircle className="w-3.5 h-3.5" /> My Profile
            </button>
            <button
              onClick={logout}
              className={`flex items-center gap-2 text-xs ${theme.mutedText} hover:text-white transition-colors`}
              data-testid="consultant-logout-btn"
            >
              <LogOut className="w-3.5 h-3.5" /> Logout
            </button>
          </div>
        </aside>
        )}
        <main className={`flex-1 min-w-0 ${clientViewActive ? "p-0" : "p-4 sm:p-6 lg:p-8"}`}>
          <Outlet />
        </main>
      </div>
      <ProfileDialog open={profileOpen} onOpenChange={setProfileOpen} user={user} onProfileUpdated={setUser} />
    </ConsultantCtx.Provider>
  );
}
